import * as React from "react";

/* v8 ignore next */
import isEqual from "fast-deep-equal/es6";

function deepClone<T>(value: T): T {
  try {
    return structuredClone(value);
    // eslint-disable-next-line no-catch-all/no-catch-all -- pre-existing violation
  } catch {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}

export interface WatchOptions {
  // Call the callback immediately, with oldValue as undefined.
  immediate?: boolean;
  // Use deep comparison to detect changes instead of referential equality:
  deep?: boolean;
}

const shallowEqual = (arr1: unknown[], arr2: unknown[]): boolean =>
  arr1.length === arr2.length && arr1.every((element, index) => arr2[index] === element);

// Can be returned from a watch callback to prevent the watch from running again.
export const HALT_WATCH = Symbol("useWatch:halt");

/** useWatch: is a hook that allows you to watch for changes in a value and run a callback when it changes.
 * Preferred to useEffect per https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes.
 * @param dependencies - The value to watch for changes.
 * @param callback - The callback to run when the value changes.
 * @param options - Options to control the behavior of the hook.
 * @param options.immediate - Call the callback immediately, with oldValue as undefined. (defaults to true)
 * @param options.deep - Use deep comparison to detect changes instead of referential equality.
 */
// eslint-disable-next-line complexity -- pre-existing violation
export function useWatch<T>(
  dependencies: T,
  // If you got here because your callback needs to return a Promise or a cleanup function, use useEffect instead.
  callback: (newValue: T, oldValue: T | undefined) => void | null | undefined | typeof HALT_WATCH,
  options?: WatchOptions,
) {
  const firstRun = React.useRef<boolean>(true);
  const oldValue = React.useRef<T | undefined>(undefined);
  const halted = React.useRef<boolean>(false);

  if (halted.current) {
    return;
  }

  let changed;
  if (options?.deep) {
    changed = !isEqual(oldValue.current, dependencies);
  } else if (Array.isArray(dependencies) && Array.isArray(oldValue.current)) {
    changed = !shallowEqual(oldValue.current, dependencies);
  } else {
    changed = oldValue.current !== dependencies;
  }

  const immediate = options?.immediate !== false;

  if (changed || (immediate && firstRun.current)) {
    if (!(firstRun.current && !immediate)) {
      const value = callback(dependencies, oldValue.current);
      if (value === HALT_WATCH) {
        halted.current = true;
        return;
      }
    }
  }

  if (changed || firstRun.current) {
    if (options?.deep) oldValue.current = deepClone(dependencies);
    else if (Array.isArray(dependencies)) oldValue.current = [...dependencies] as typeof dependencies;
    else oldValue.current = dependencies;

    firstRun.current = false;
  }
}
